import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Landmark } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

/**
 * Tax paid to tax offices, with the bank lines behind the figure
 * (GET /reports/tax-paid; rules in backend services/tax_payments.py).
 * Used on the Costs tab of the business and personal Profit & Loss pages.
 */

const CURRENCY_SYMBOLS = { GBP: '\u00a3', EUR: '\u20ac', USD: '$', DKK: 'kr' };

const formatAmount = (value, currency = 'GBP') => {
  const sym = CURRENCY_SYMBOLS[currency] || currency + ' ';
  return `${sym}${Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ENTITY_LABELS = {
  br_ltd: 'BR Ltd',
  eci: 'ECI',
  bi_ltd: 'BI Ltd',
  bi_uk: 'BI UK',
  bi_immo: 'Bonjour Immobilier',
  bi_sas: 'BI SAS',
  fan_zhang: 'Fan Zhang',
  fan_zhang_piotr_kacki: 'FZ & PK',
  piotr_kacki: 'Piotr Kacki',
};

export const TAX_PAID_HELP = 'Payments to HMRC, DGFiP (impots.gouv), Finanzamt and other tax offices, '
  + 'read from the bank lines. A returned direct debit cancels its payment. Refunds, URSSAF '
  + 'contributions and bank garnishments are not counted.';

// Overall > Taxes > Tax paid (Fan, 2026-10-27): one dropdown per company,
// total in the report currency (paid_base, returned debits net off).
function TaxPaidByEntity({ lines, fmt }) {
  const [openIds, setOpenIds] = useState(() => new Set());
  const groups = [];
  const byId = {};
  lines.forEach((ln) => {
    if (!byId[ln.entity_id]) {
      byId[ln.entity_id] = { id: ln.entity_id, items: [], total: 0 };
      groups.push(byId[ln.entity_id]);
    }
    byId[ln.entity_id].items.push(ln);
    byId[ln.entity_id].total += Number(ln.paid_base) || 0;
  });
  groups.sort((a, b) => b.total - a.total);
  const toggle = (id) => setOpenIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Company</TableHead>
          <TableHead>Paid to</TableHead>
          <TableHead className="text-right">Amount</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {groups.length === 0 ? (
          <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No tax payments in the bank feeds for this period.</TableCell></TableRow>
        ) : groups.map((g) => {
          const isOpen = openIds.has(g.id);
          return (
            <React.Fragment key={g.id}>
              <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggle(g.id)} data-testid="tax-paid-company-row">
                <TableCell className="font-medium">
                  <span className="inline-flex items-center gap-1">
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    {ENTITY_LABELS[g.id] || g.id}
                  </span>
                  <span className="ml-2 text-xs text-muted-foreground font-normal">
                    {g.items.length} {g.items.length === 1 ? 'payment' : 'payments'}
                  </span>
                </TableCell>
                <TableCell />
                <TableCell className="text-right font-mono text-sm font-medium">{fmt(g.total)}</TableCell>
              </TableRow>
              {isOpen && g.items.map((ln, i) => (
                <TableRow key={(ln.transaction_id || '') + i} className="bg-muted/20 text-xs">
                  <TableCell className="pl-8 font-mono">{ln.date}</TableCell>
                  <TableCell title={ln.label}>
                    {ln.authority} <span className="text-muted-foreground">({ln.kind_label}{ln.role === 'reversal' ? ', returned' : ''})</span>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {ln.role === 'reversal' ? '-' : ''}{formatAmount(Math.abs(ln.amount), ln.currency || 'GBP')}
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}

export function TaxPaidPanel({ taxPaid, fmt, groupByEntity = false }) {
  if (!taxPaid) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">Tax payments could not be loaded.</CardContent>
      </Card>
    );
  }
  const lines = taxPaid.lines || [];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="tax-paid-panel">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Landmark className="h-4 w-4" />Tax paid {taxPaid.year}</CardTitle>
          <CardDescription title={TAX_PAID_HELP}>{fmt(taxPaid.total || 0)}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(taxPaid.by_authority || []).map((row) => (
            <div key={row.authority + row.kind} className="flex justify-between text-sm">
              <span>{row.authority} <span className="text-muted-foreground">({row.kind_label})</span></span>
              <span className="font-mono">{fmt(row.amount)}</span>
            </div>
          ))}
          {(taxPaid.refunds || []).length > 0 && (
            <p className="text-xs text-muted-foreground pt-2 border-t">
              {taxPaid.refunds.length} refund{taxPaid.refunds.length === 1 ? '' : 's'} from a tax office received, not deducted.
            </p>
          )}
        </CardContent>
      </Card>
      <Card className="lg:col-span-2">
        <CardContent className="p-0">
          {groupByEntity ? <TaxPaidByEntity lines={lines} fmt={fmt} /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Paid to</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No tax payments in the bank feeds for this period.</TableCell></TableRow>
              ) : lines.map((ln, i) => (
                <TableRow key={(ln.transaction_id || '') + i}>
                  <TableCell className="font-mono text-xs">{ln.date}</TableCell>
                  <TableCell className="text-sm" title={ln.label}>
                    {ln.authority} <span className="text-muted-foreground">({ln.kind_label}{ln.role === 'reversal' ? ', returned' : ''})</span>
                  </TableCell>
                  <TableCell className="text-sm">{ENTITY_LABELS[ln.entity_id] || ln.entity_id}</TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {ln.role === 'reversal' ? '-' : ''}{formatAmount(Math.abs(ln.amount), ln.currency || 'GBP')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

